import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { ServiceBase } from './service.base';
import {
  EditRecipe, GenerateOperation, GenerateOptions, ImageAsset, ImageQuota, RenderJob,
  SCENE_DETAIL_MAX, pocQuotaFor, quotaBlock,
} from '../components/artist-components/image-studio/image-studio.model';

/**
 * Image Studio API client.
 *
 * The endpoints are BUILT AND LIVE. Every call goes through this service — components
 * never touch HttpClient (.conventions.md).
 *
 * Responses ARE wrapped in the standard ResponseObject envelope. The earlier note here
 * said otherwise, citing the /ArtistProduct/remove-background precedent; that was wrong.
 * Every other service in this app unwraps the envelope, and these endpoints being the
 * exception would have been the surprise. `unwrap()` below handles it in one place.
 *
 * NOTE: the API returns HTTP 200 even on handled errors — the outcome lives in
 * `Success`, never the status code. A client that trusts the status renders a blank
 * screen with no error state, which is why unwrap() raises instead.
 */
@Injectable({ providedIn: 'root' })
export class ImageStudioService extends ServiceBase {

  /** Mock is off — the endpoints are real. Flip back only to work offline. */
  public useMock = false;

  /**
   * TESTING ONLY — never blocks and never spends the allowance.
   *
   * Set by `?nolimits=1` on the URL, so the flow can be exercised repeatedly without
   * burning through 2/day. Client-side only: the real server enforces its own limits
   * regardless of what this says.
   */
  public bypassQuota = false;

  private readonly base = `${environment.BASE_API_PATH}`;

  /** Mock state — a per-product asset list that survives for the session. */
  private mockAssets = new Map<string, ImageAsset[]>();
  // POC: the plan is hardcoded here too. Real resolution reads the artist's subscription.
  private mockQuota: ImageQuota = pocQuotaFor('Free');
  private mockJobs = new Map<string, RenderJob>();
  private seq = 0;

  constructor(private http: HttpClient, authService: AuthService) {
    super(authService);
  }

  /**
   * Pulls the payload out of the ResponseObject envelope, and turns `Success: false`
   * into a thrown error so callers can use normal RxJS error handling.
   *
   * This exists because the API answers HTTP 200 on handled errors: without it a failed
   * call looks identical to a successful one that happened to return nothing, and the
   * UI silently shows an empty state. `Errors[0]` carries a stable machine code
   * (IMAGE_ASSET_NOT_FOUND, IMAGE_ALLOWANCE_SPENT, IMAGE_PROVIDER_FAILED …) — branch on
   * that, never on the message text, which is user-facing prose and will change.
   */
  private unwrap<T>(source: Observable<any>): Observable<T> {
    return source.pipe(map(res => {
      if (res && res.Success === false) {
        throw { code: (res.Errors && res.Errors[0]) || 'UNKNOWN', message: res.ResponseMessage };
      }
      return (res && Object.prototype.hasOwnProperty.call(res, 'ResponseData'))
        ? res.ResponseData as T
        : res as T;
    }));
  }

  // ---------------------------------------------------------------- assets

  /**
   * Upload the untouched original — multipart, field name `file`, up to 120 MB.
   * Everything else derives from this, and it is never altered again.
   *
   * Deliberately does NOT use RequestHeaders(): that sets Content-Type: application/json,
   * which on a FormData body suppresses the browser's multipart boundary and the server
   * cannot parse the parts. Same trap that had every upload in this app returning 401.
   */
  public uploadOriginal(artistProductId: string, file: File): Observable<ImageAsset> {
    const formData = new FormData();
    formData.append('file', file);
    const token = this.authService.getAccessToken();
    const headers: Record<string, string> = { 'Accept': 'application/json' };
    if (token) { headers['Authorization'] = 'Bearer ' + token; }
    return this.unwrap<ImageAsset>(this.http.post<any>(
      `${this.base}/ImageAsset/${artistProductId}/original`, formData, { headers }));
  }

  /**
   * Adopts an image the artwork ALREADY has as its `Original` — by reference, moving no bytes,
   * and idempotently: call it twice and you get the same asset back.
   *
   * Replaces the old bootstrap, which established identity by RE-CREATING it — fetching the
   * artwork's photo into the browser and uploading it back, producing a new storage object and
   * a new `Original` row every time it could not textually match a URL. One bowl accumulated
   * eleven, and because each new lineage was disconnected from the cover, promotion could never
   * reach the artwork. Matching on a filename is fragile in a way an id is not.
   *
   * The copy was never protecting anything: assets are append-only and deletion is soft,
   * artist-initiated and refused on the current image. It just charged a full-size download AND
   * upload on every open — twice over on a phone.
   */
  public adoptExisting(artistProductId: string, imageUrl: string): Observable<ImageAsset> {
    if (!this.useMock) {
      return this.unwrap<ImageAsset>(this.http.post<any>(
        `${this.base}/ImageAsset/${artistProductId}/adopt`, { ImageUrl: imageUrl }, this.RequestHeaders()));
    }
    const seeded = this.seedMock(artistProductId, imageUrl);
    return of(seeded[seeded.length - 1]).pipe(delay(80));
  }

  /** Every asset for an artwork, newest first. The `Original` is always present. */
  public getAssets(artistProductId: string, currentImageUrl = ''): Observable<ImageAsset[]> {
    if (!this.useMock) {
      return this.unwrap<ImageAsset[]>(this.http.get<any>(
        `${this.base}/ImageAsset/${artistProductId}/fetch`, this.RequestHeaders()));
    }
    return of(this.seedMock(artistProductId, currentImageUrl)).pipe(delay(120));
  }

  /**
   * Promote a provisional generated asset to the artwork's image. This is the only
   * call that changes what the public sees, and it happens only on an explicit accept.
   */
  public acceptAsset(assetId: string): Observable<ImageAsset> {
    if (!this.useMock) {
      return this.unwrap<ImageAsset>(this.http.post<any>(
        `${this.base}/ImageAsset/${assetId}/accept`, {}, this.RequestHeaders()));
    }
    const asset = this.findMockAsset(assetId);
    if (!asset) return throwError(() => new Error('asset-not-found'));
    // Status is the artist's decision about THIS asset — it is NOT "is current".
    // Demoting the previously-accepted asset to Declined here made the original
    // vanish from the versions list the moment anything was accepted, which breaks
    // the whole non-destructive promise. Accepting one asset says nothing about
    // the others; IsCurrent is the separate pointer to what the artwork shows.
    asset.Status = 'Accepted';
    // Mirrors the server's Decide(): exactly one current image per artwork.
    for (const sibling of this.listFor(asset.ArtistProductId)) {
      sibling.IsCurrent = sibling.Id === asset.Id;
    }
    return of(asset).pipe(delay(150));
  }

  /**
   * "Use THIS one." The explicit choice, distinct from `acceptAsset`.
   *
   * Accepting says "this AI result is good" and leaves the server to infer from the lineage
   * whether it also becomes the artwork's image. That inference was right often enough to keep
   * and wrong often enough to strand artists: on 2026-08-22 a bowl had eleven `Original` rows
   * (one per studio session), so every new version sat in a lineage disconnected from the cover,
   * the rule correctly answered "not the cover's lineage", and an accepted catalogue image never
   * reached the artwork with no way to say otherwise. This is that way.
   */
  public setMainAsset(assetId: string): Observable<ImageAsset> {
    if (!this.useMock) {
      return this.unwrap<ImageAsset>(this.http.post<any>(
        `${this.base}/ImageAsset/${assetId}/set-main`, {}, this.RequestHeaders()));
    }
    const asset = this.findMockAsset(assetId);
    if (!asset) return throwError(() => new Error('asset-not-found'));
    asset.Status = 'Accepted';
    for (const sibling of this.listFor(asset.ArtistProductId)) {
      sibling.IsCurrent = sibling.Id === asset.Id;
    }
    return of(asset).pipe(delay(150));
  }

  /**
   * Removes one of the artist's own adjustments. Soft on the server, and refused for AI results
   * (they cost money and cannot be re-derived) and for the current image.
   */
  public deleteAsset(assetId: string): Observable<ImageAsset> {
    if (!this.useMock) {
      return this.unwrap<ImageAsset>(this.http.delete<any>(
        `${this.base}/ImageAsset/${assetId}`, this.RequestHeaders()));
    }
    const asset = this.findMockAsset(assetId);
    if (!asset) return throwError(() => new Error('asset-not-found'));
    const list = this.listFor(asset.ArtistProductId);
    const index = list.findIndex(a => a.Id === assetId);
    if (index >= 0) list.splice(index, 1);
    return of(asset).pipe(delay(120));
  }

  /** The artist's own note on a version. Blank clears it. */
  public setAssetLabel(assetId: string, label: string): Observable<ImageAsset> {
    if (!this.useMock) {
      return this.unwrap<ImageAsset>(this.http.patch<any>(
        `${this.base}/ImageAsset/${assetId}/label`, { Label: label }, this.RequestHeaders()));
    }
    const asset = this.findMockAsset(assetId);
    if (!asset) return throwError(() => new Error('asset-not-found'));
    asset.Label = label && label.trim() ? label.trim() : null;
    return of(asset).pipe(delay(80));
  }

  /** Decline keeps the asset (it cost money) but never shows it as the artwork. */
  public declineAsset(assetId: string): Observable<ImageAsset> {
    if (!this.useMock) {
      return this.unwrap<ImageAsset>(this.http.post<any>(
        `${this.base}/ImageAsset/${assetId}/decline`, {}, this.RequestHeaders()));
    }
    const asset = this.findMockAsset(assetId);
    if (!asset) return throwError(() => new Error('asset-not-found'));
    asset.Status = 'Declined';
    return of(asset).pipe(delay(120));
  }

  // ---------------------------------------------------------------- quota

  /**
   * Read BEFORE offering a paid action, so the artist is never surprised.
   *
   * There is no quota endpoint in the live contract yet — swagger exposes only
   * `/ImageGenerate/catalogue`. Until metering lands we fall back to the POC limits so
   * the counter still reads correctly in the UI; the SERVER remains the authority and
   * a 429 will overwrite these figures with the real ones.
   */
  public getQuota(): Observable<ImageQuota> {
    return of(JSON.parse(JSON.stringify(this.mockQuota))).pipe(delay(80));
  }

  // ---------------------------------------------------------------- jobs

  /**
   * Deterministic render from the ORIGINAL. Free and unmetered — correction never
   * costs the artist anything, which is why the UI leads with it.
   */
  public renderRecipe(recipe: EditRecipe): Observable<RenderJob> {
    if (!this.useMock) {
      // The recipe is NESTED, and SourceAssetId travels beside it — the server binds
      // RenderRequest{SourceAssetId, Recipe, IdempotencyKey}. Posting the bare recipe
      // (as this did) left SourceAssetId null and every render was refused with
      // MISSING_PARAMETERS before anything was queued.
      //
      // No IdempotencyKey here on purpose: the key we hold is per operation+source, so
      // it would still match after the artist moved a slider, and the server would hand
      // back the job for the PREVIOUS settings. Correction is free, so a duplicate costs
      // only CPU; returning the wrong image would cost trust.
      return this.unwrap<RenderJob>(this.http.post<any>(
        `${this.base}/ImageRecipe/render`,
        { SourceAssetId: recipe.SourceAssetId, Recipe: recipe },
        this.RequestHeaders()));
    }
    return this.startMockJob(recipe.SourceAssetId, 'Corrected', null).pipe(delay(150));
  }

  /**
   * Generative op. Metered. The SERVER picks the provider for the operation — clients
   * name the outcome, never the model, so both clients can't drift apart on prompts.
   */
  public generate(
    operation: GenerateOperation,
    sourceAssetId: string,
    options: GenerateOptions = {},
  ): Observable<RenderJob> {
    if (!this.useMock) {
      const path = operation === 'Catalogue' ? 'catalogue' : 'scene';

      // Generation costs money, so the same user action must carry the same key: a retry
      // over a flaky connection then returns the ORIGINAL job instead of buying a second image.
      const body: Record<string, string> = {
        SourceAssetId: sourceAssetId,
        IdempotencyKey: this.idempotencyKeyFor(operation, sourceAssetId),
      };

      // Sent for a product shot only. A catalogue image is DEFINED by having no setting, so a
      // PromptId on that path would be an instruction to change how catalogue images look —
      // a decision that is versioned server-side and is not the client's to make.
      if (operation === 'Scene') {
        if (options.promptId) { body['PromptId'] = options.promptId; }
        const detail = (options.sceneDescription || '').trim();
        // Trimmed here as well as on the server: the server's cap is the one that holds, but a
        // request that carries text the artist cannot see having been cut is worse than one that
        // sends what the box showed them.
        if (detail) { body['SceneDescription'] = detail.slice(0, SCENE_DETAIL_MAX); }
      }

      return this.unwrap<RenderJob>(this.http.post<any>(
        `${this.base}/ImageGenerate/${path}`, body, this.RequestHeaders()));
    }
    if (!this.bypassQuota && quotaBlock(this.mockQuota) !== 'none') {
      return throwError(() => ({ status: 429, error: { ...this.mockQuota } }));
    }
    // Charged at generation, so a later decline does NOT refund. Own-key is unmetered.
    if (!this.bypassQuota && this.mockQuota.Source === 'Plan') {
      this.spend(this.mockQuota.Daily);
      this.spend(this.mockQuota.Monthly);
    }
    return this.startMockJob(sourceAssetId, 'Generated', operation).pipe(delay(200));
  }

  /** Poll a job. Real implementation is supplemented by FCM push on completion. */
  public getJob(jobId: string): Observable<RenderJob> {
    if (!this.useMock) {
      return this.unwrap<RenderJob>(this.http.get<any>(
        `${this.base}/ImageRecipe/job/${jobId}`, this.RequestHeaders()));
    }
    const job = this.mockJobs.get(jobId);
    if (!job) return throwError(() => new Error('job-not-found'));
    // Mock progression: first poll is Running, second Succeeded.
    if (job.Status === 'Queued') { job.Status = 'Running'; }
    else if (job.Status === 'Running') { job.Status = 'Succeeded'; }
    return of({ ...job }).pipe(delay(600));
  }

  /**
   * One key per (operation, source) attempt. Held for the session so an immediate retry of
   * the SAME action reuses it — that is what stops a double-tap or a dropped connection
   * being charged twice. A deliberate second generation of the same artwork gets a new key
   * because the caller clears it via `newAttempt()`.
   */
  private idempotencyKeys = new Map<string, string>();

  private idempotencyKeyFor(operation: GenerateOperation, sourceAssetId: string): string {
    const k = `${operation}:${sourceAssetId}`;
    if (!this.idempotencyKeys.has(k)) {
      this.idempotencyKeys.set(k, `${k}:${Date.now()}:${Math.random().toString(36).slice(2, 10)}`);
    }
    return this.idempotencyKeys.get(k)!;
  }

  /** Call when the artist deliberately asks for another go — a new attempt, a new key. */
  public newAttempt(operation: GenerateOperation, sourceAssetId: string): void {
    this.idempotencyKeys.delete(`${operation}:${sourceAssetId}`);
  }

  // ---------------------------------------------------------------- mock internals

  private listFor(productId: string): ImageAsset[] {
    return this.mockAssets.get(productId) || [];
  }

  private findMockAsset(assetId: string): ImageAsset | undefined {
    for (const list of this.mockAssets.values()) {
      const hit = list.find(a => a.Id === assetId);
      if (hit) return hit;
    }
    return undefined;
  }

  /** The mock's Original stands in for the artwork's existing image. */
  private seedMock(productId: string, currentImageUrl: string): ImageAsset[] {
    if (!this.mockAssets.has(productId)) {
      this.mockAssets.set(productId, [{
        Id: `asset-${productId}-original`,
        ArtistProductId: productId,
        Kind: 'Original',
        Status: 'Accepted',
        // It is the artwork's image today, so the pointer already points here.
        IsCurrent: true,
        Url: currentImageUrl,
        Width: 3024, Height: 4032,
        CreatedDate: new Date().toISOString(),
        SourceAssetId: null, DerivativeName: null, Operation: null,
        Provider: null, PromptId: null,
        Label: 'Your original photograph',
      }]);
    }
    return [...this.listFor(productId)].reverse();
  }

  private startMockJob(
    sourceAssetId: string,
    kind: 'Corrected' | 'Generated',
    operation: GenerateOperation | null,
  ): Observable<RenderJob> {
    const source = this.findMockAsset(sourceAssetId);
    const productId = source ? source.ArtistProductId : 'unknown';
    const assetId = `asset-${++this.seq}`;
    const jobId = `job-${this.seq}`;

    this.listFor(productId).push({
      Id: assetId,
      ArtistProductId: productId,
      Kind: kind,
      // Corrections are deterministic and free, so they're accepted immediately.
      // Generated results are provisional until the artist chooses.
      Status: kind === 'Generated' ? 'Provisional' : 'Accepted',
      // A correction becomes the artwork's image; a generated result never does until
      // the artist accepts it. Mirrors the worker.
      IsCurrent: kind === 'Corrected',
      Url: source ? source.Url : '',
      Width: 1200, Height: 1200,
      CreatedDate: new Date().toISOString(),
      SourceAssetId: sourceAssetId,
      DerivativeName: kind === 'Corrected' ? 'master' : null,
      Operation: kind === 'Generated' ? (operation === 'Scene' ? 'scene' : 'catalogue') : null,
      Provider: kind === 'Generated' ? 'Google' : null,
      PromptId: kind === 'Generated'
        ? (operation === 'Scene' ? 'ai-scene-shelf-v1' : 'ai-catalogue-white-v1') : null,
      // Mirrors ImageLabelWriter server-side: a version always arrives describing itself, and
      // names the outcome the artist asked for rather than the operation's internal word.
      Label: kind === 'Generated'
        ? (operation === 'Scene' ? 'Product shot — Google' : 'Catalogue image — Google')
        : 'Adjusted',
    });

    // One current image per artwork — a correction takes the pointer from the original.
    if (kind === 'Corrected') {
      for (const sibling of this.listFor(productId)) {
        sibling.IsCurrent = sibling.Id === assetId;
      }
    }

    const job: RenderJob = { JobId: jobId, Status: 'Queued', ResultAssetId: assetId, Error: null };
    this.mockJobs.set(jobId, job);
    return of({ ...job }).pipe(map(j => j));
  }

  private spend(window: { Used: number; Limit: number; Remaining: number }): void {
    window.Used++;
    window.Remaining = Math.max(0, window.Limit - window.Used);
  }

  /** Test seam — lets specs start from a known quota without reaching into privates. */
  public setMockQuota(quota: ImageQuota): void {
    this.mockQuota = JSON.parse(JSON.stringify(quota));
  }
}
